import { Injectable, Logger, Optional } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService, DEFAULT_STORE_ID } from '../tenancy/store-context.service';

export interface RenderedTemplate {
  subject: string;
  html: string;
}

// Layout rows are a single global pair, not per-store like every other
// template — one Header + one Footer for the whole platform, looked up
// without storeId scoping (see plan: "Header/Content/Footer composition").
const EMAIL_HEADER_CODE = 'email_header';
const EMAIL_FOOTER_CODE = 'email_footer';
const LAYOUT_CODES = new Set([EMAIL_HEADER_CODE, EMAIL_FOOTER_CODE]);

// Auth / merchant-lifecycle codes always resolve against DEFAULT_STORE_ID,
// never the ambient StoreContextService — these fire from platform-level
// actions (a brand-new merchant has no store yet at registerMerchant() time;
// a platform admin approving/rejecting one may have a completely unrelated
// merchant's store selected in their own store-switcher). Confirmed live:
// approving a merchant while a different store was selected sent that
// store's id as x-store-id (via tenant-http.interceptor.ts), and the
// merchant_approved lookup below silently scoped to it instead of
// store_demo — which is where these templates actually live — producing a
// "no active/published template found" failure even though the template
// exists. Store-specific templates (order_placed, cart_abandonment, etc.)
// are deliberately NOT in this set — those fire in a real customer-facing
// store request and should keep using that store's own copy.
const PLATFORM_SCOPED_CODES = new Set([
  'welcome_registration',
  'password_reset',
  'password_changed',
  'email_verification',
  'merchant_registration_pending',
  'merchant_approved',
  'merchant_rejected',
  'merchant_application_received',
]);

// Same font stack + primary color as admin/src/styles/_variables.scss's
// --font/--c-primary — emails should look like they came from the same
// product as the admin UI, not a generic default. The fallback chain
// (system-ui → Segoe UI → Helvetica → Arial) is what actually renders in
// Outlook/Gmail/Apple Mail, since none of them load the 'Inter' webfont —
// Segoe UI (Windows/Outlook's native font) is visually close to Inter, so
// the fallback still reads as "the same font" everywhere it matters.
const EMAIL_FONT_STACK =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
const DEFAULT_PRIMARY_COLOR = '#6D5DF6';

// Outer shell width. 800px per the design spec — note most clients cap the
// reading pane narrower than this, so the max-width + width:100% pairing
// below is what actually keeps it from overflowing on mobile.
const EMAIL_WIDTH = 800;

// {{logoBlock}} is computed, not stored: an <img> when a logo URL is
// configured, otherwise a text wordmark. This gives real conditional
// behaviour without adding a templating engine — and avoids the two bad
// alternatives for a default icon (an emoji, which renders as a yellow
// glyph, or an SVG/base64 data URI, which Gmail strips entirely).
// White, not the brand color — this sits on the brand-colored header band.
function buildLogoBlock(logoUrl: string, storeName: string): string {
  if (logoUrl) {
    return `<img src="${logoUrl}" alt="${storeName}" width="150" style="display:block;border:0;max-width:150px;height:auto;margin:0 auto;" />`;
  }

  return `<span style="display:inline-block;color:#ffffff;font-family:${EMAIL_FONT_STACK};font-size:28px;font-weight:700;letter-spacing:-0.02em;line-height:1.2;">${storeName}</span>`;
}

// Table-based layout, not divs — the one construct every email client
// (including old desktop Outlook's Word rendering engine) reliably honors
// for centering/width. Each of header/content/footer is its own complete,
// valid table stacked vertically, rather than one continuous table split
// across three DB rows — that would break the instant an admin's edited
// content isn't a well-formed <tr>/<td> fragment.
function wrapContent(html: string): string {
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#f1f5f9;"><tr><td align="center" style="padding:0;"><table role="presentation" width="${EMAIL_WIDTH}" cellpadding="0" cellspacing="0" style="width:100%;max-width:${EMAIL_WIDTH}px;margin:0 auto;"><tr><td align="center" style="background-color:#ffffff;padding:40px 48px 44px;font-family:${EMAIL_FONT_STACK};color:#334155;font-size:16px;line-height:1.65;text-align:center;">${html}</td></tr></table></td></tr></table>`;
}

// {{variableName}} substitution only — no templating engine dependency,
// matching the plain template-literal approach the password-reset email
// already used before it moved into the DB. A missing template must never
// throw: callers treat a null return as "skip this send," same fail-soft
// posture as EmailService with no SMTP configured.
@Injectable()
export class TemplateRenderService {
  private readonly logger = new Logger(TemplateRenderService.name);

  constructor(
    private readonly prisma: PrismaService,
    @Optional() private readonly storeContext?: StoreContextService
  ) {}

  // `override` lets Send Test Email render unsaved draft content (or content
  // the caller already has in hand) through the exact same header/footer
  // composition a real send would use, without writing it to the DB first.
  async render(
    typeCode: string,
    variables: Record<string, string>,
    override?: { subject: string; bodyHtml: string }
  ): Promise<RenderedTemplate | undefined> {
    // See PLATFORM_SCOPED_CODES above — auth/merchant-lifecycle emails must
    // never pick up whatever store the ambient request happens to be scoped
    // to (a platform admin's own store-switcher selection, a registering
    // merchant with no store yet, ...).
    const storeId = PLATFORM_SCOPED_CODES.has(typeCode) ? DEFAULT_STORE_ID : this.storeId();

    const content =
      override ?? (await this.findTemplate(typeCode, { global: LAYOUT_CODES.has(typeCode), storeId }));

    if (!content) {
      this.logger.warn(`No active template found for type "${typeCode}" — skipping email`);
      return undefined;
    }

    // Branding settings (Settings dialog) ride along as extra variables on
    // every render — a caller-supplied logoUrl/primaryColor still wins if
    // ever provided, since it's spread after these defaults.
    const branding = await this.brandingVariables(storeId);
    variables = { ...branding, ...variables };

    const subject = this.substitute(content.subject, variables);
    let html = this.substitute(content.bodyHtml, variables);

    // Layout rows themselves don't get wrapped in a header/footer — only
    // business content does.
    if (!LAYOUT_CODES.has(typeCode)) {
      const [header, footer] = await Promise.all([
        this.findTemplate(EMAIL_HEADER_CODE, { global: true, storeId }),
        this.findTemplate(EMAIL_FOOTER_CODE, { global: true, storeId }),
      ]);

      // Missing layout rows fall back to content-only — chrome is
      // decoration, never a reason to block a send.
      const headerHtml = header ? this.substitute(header.bodyHtml, variables) : '';
      const footerHtml = footer ? this.substitute(footer.bodyHtml, variables) : '';
      html = `${headerHtml}${wrapContent(html)}${footerHtml}`;
    }

    return { subject, html };
  }

  private async findTemplate(
    typeCode: string,
    options: { global: boolean; storeId: string }
  ): Promise<{ subject: string; bodyHtml: string } | undefined> {
    try {
      const template = await this.prisma.template.findFirst({
        where: {
          ...(options.global ? {} : { storeId: options.storeId }),
          isActive: true,
          isPublished: true,
          templateType: { code: typeCode, isActive: true },
        },
      });

      if (template) return template;

      // No store override — fall back to the type's system default.
      //
      // This is what lets a store hold NO template rows at all. Before, every
      // store needed its own copy of all 21 templates or its emails silently
      // failed with "no active/published template found"; now the absence of a
      // row is the normal case and means "use the platform default".
      const type = await this.prisma.templateType.findFirst({
        where: { code: typeCode, isActive: true },
        select: { defaultSubject: true, defaultBodyHtml: true },
      });

      if (type?.defaultSubject && type.defaultBodyHtml) {
        return { subject: type.defaultSubject, bodyHtml: type.defaultBodyHtml };
      }

      return undefined;
    } catch (error) {
      // Fail soft like AuditService/EmailService — a DB hiccup or a template
      // trigger firing before the templates table exists (e.g. a test DB
      // without this migration applied) must never fail the business action
      // (registration, order status update, ...) that triggered the email.
      this.logger.warn(`Failed to load template "${typeCode}": ${(error as Error).message}`);
      return undefined;
    }
  }

  private async brandingVariables(storeId: string): Promise<Record<string, string>> {
    try {
      const [settings, store] = await Promise.all([
        /*
         * Scoped to the store the email is for. `findFirst()` with no filter
         * returned whichever row the database handed back first, so the moment
         * a second merchant configured email branding, some merchants' order
         * confirmations would have gone out carrying another merchant's logo
         * and sender name. There is one row today, which is the only reason it
         * has not happened yet.
         */
        this.prisma.emailSettings.findFirst({ where: { storeId } }),
        this.prisma.store.findFirst({ where: { id: storeId }, select: { name: true } }),
      ]);

      const logoUrl = settings?.logoUrl ?? '';
      const primaryColor = settings?.primaryColor || DEFAULT_PRIMARY_COLOR;
      const storeName = settings?.senderName || store?.name || 'Shopeee';

      return {
        logoUrl,
        primaryColor,
        storeName,
        logoBlock: buildLogoBlock(logoUrl, storeName),
      };
    } catch {
      return {
        logoUrl: '',
        primaryColor: DEFAULT_PRIMARY_COLOR,
        storeName: 'Shopeee',
        logoBlock: buildLogoBlock('', 'Shopeee'),
      };
    }
  }

  private substitute(text: string, variables: Record<string, string>): string {
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
      Object.prototype.hasOwnProperty.call(variables, name) ? variables[name] : match
    );
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? DEFAULT_STORE_ID;
  }
}
